// packages/utils/src/shared/resultHttp.ts
import type { Result } from '@tourtask/types/result';
import type { ErrorKind } from '@tourtask/types/errorKinds';

/**
 * The `Result<T>` envelope carried over HTTP: the status a JSON endpoint answers
 * a failure with (apps/api `/v1` through `respond.ts`, backoffice Route
 * Handlers), and the check a browser caller runs on what came back. Type-only
 * imports, so it is safe in client bundles.
 */

// Exhaustive by type: a new ErrorKind does not compile until it has a status.
const STATUS_BY_KIND: Record<ErrorKind, number> = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  VALIDATION: 422,
  ABORTED: 422,
  RATE_LIMIT: 429,
  OFFLINE: 503,
  TIMEOUT: 504,
  UNKNOWN: 500,
  UPSTREAM: 500,
  INTERNAL: 500,
};

/**
 * HTTP status for a failure kind, so the status and the body's
 * `success: false` always agree (docs/public-api-conventions.md § Response shape).
 */
export function kindToStatus(kind: ErrorKind): number {
  return STATUS_BY_KIND[kind];
}

/**
 * Whether a parsed JSON body is a `Result` envelope. Lets a browser caller tell
 * the endpoint's own answer apart from any other JSON that reached it (a
 * gateway's or proxy's error body), so it never reads `error.kind` off a body
 * that has none.
 */
export function isResultEnvelope<T = unknown>(value: unknown): value is Result<T> {
  if (typeof value !== 'object' || value === null || !('success' in value)) return false;
  if (value.success === true) return true;
  return (
    value.success === false &&
    'error' in value &&
    typeof value.error === 'object' &&
    value.error !== null
  );
}
